import * as SliderPrimitive from "@radix-ui/react-slider";
import * as React from "react";

import { cn } from "@bcc/lib/utils";

import { setSliderTestId } from "./slider.utils";

interface SliderProps extends React.ComponentProps<typeof SliderPrimitive.Root> {
  testId?: string;
  /** Overrides the track's background, e.g. a custom gradient in place of `bg-muted`. */
  trackClassName?: string;
  trackStyle?: React.CSSProperties;
  /** Overrides the filled range, e.g. hiding it when the track itself already encodes meaning. */
  rangeClassName?: string;
  /** Overrides each thumb, e.g. hiding it before the caller has a real value to show. */
  thumbClassName?: string;
  /** Overrides the announced value, e.g. "Not set" while a real value is faked for display only. */
  valueText?: string;
}

function Slider({
  className,
  defaultValue,
  value,
  min = 0,
  max = 100,
  testId,
  trackClassName,
  trackStyle,
  rangeClassName,
  thumbClassName,
  valueText,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  ...props
}: Readonly<SliderProps>) {
  const values = React.useMemo(
    () => (Array.isArray(value) ? value : Array.isArray(defaultValue) ? defaultValue : [min, max]),
    [value, defaultValue, min, max]
  );

  return (
    <SliderPrimitive.Root
      data-slot="slider"
      data-testid={testId ?? setSliderTestId("root")}
      defaultValue={value === undefined ? values : undefined}
      value={value}
      min={min}
      max={max}
      className={cn(
        "relative flex w-full touch-none items-center select-none data-[disabled]:opacity-50 data-[orientation=vertical]:h-full data-[orientation=vertical]:min-h-44 data-[orientation=vertical]:w-auto data-[orientation=vertical]:flex-col",
        className
      )}
      {...props}
    >
      <SliderPrimitive.Track
        data-slot="slider-track"
        style={trackStyle}
        className={cn(
          "bg-muted relative grow overflow-hidden rounded-full data-[orientation=horizontal]:h-1.5 data-[orientation=horizontal]:w-full data-[orientation=vertical]:h-full data-[orientation=vertical]:w-1.5",
          trackClassName
        )}
      >
        <SliderPrimitive.Range
          data-slot="slider-range"
          className={cn("bg-primary absolute data-[orientation=horizontal]:h-full data-[orientation=vertical]:w-full", rangeClassName)}
        />
      </SliderPrimitive.Track>
      {/* role="slider" lives on the Thumb, not Root, so an accessible name
          only reaches assistive tech (and RTL's getByRole name matcher) if
          aria-label/aria-labelledby is applied here instead of spread onto
          Root. */}
      {values.map((_, index) => (
        <SliderPrimitive.Thumb
          data-slot="slider-thumb"
          data-testid={setSliderTestId(`thumb-${String(index)}`)}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          aria-valuetext={valueText}
          key={index}
          className={cn(
            "border-primary bg-background ring-ring/50 block size-4 shrink-0 rounded-full border shadow-sm transition-[color,box-shadow] hover:ring-4 focus-visible:ring-4 focus-visible:outline-hidden disabled:pointer-events-none disabled:opacity-50",
            thumbClassName
          )}
        />
      ))}
    </SliderPrimitive.Root>
  );
}

export { Slider };
